import { Application } from '@platform/client';
import {
  OrganizationApplicationParameters,
  OrganizationExperienceHopeEnum,
} from '@platform/client/schemas';
import { ErrorBox, FormsInputDropdown, InputDropdownItem, TagSelect } from '@platform/components';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { ApplicationBackground } from '../../ApplicationBackground';
import { ApplicationPageContent } from '../../components/ApplicationPageContent';
import { OrgSubmissionShowcase } from './OrgSubmissionShowcase';

type FormValues = {
  experienceHope: OrganizationExperienceHopeEnum[];
  referralMethod: string;
};

const REFERRAL_METHOD_OPTIONS: InputDropdownItem[] = [
  { text: 'Search engine', textValue: 'Search engine' },
  { text: 'LinkedIn', textValue: 'LinkedIn' },
  { text: 'Referral from a colleague or friend', textValue: 'Referral from a colleague or friend' },
  { text: 'From a partner or organization', textValue: 'From a partner or organization' },
  { text: 'Accelerator or incubator program', textValue: 'Accelerator or incubator program' },
  { text: 'Other', textValue: 'Other' },
];

const ORG_HOPE_OPTIONS = [
  { value: OrganizationExperienceHopeEnum['increase-impact'], label: 'Increase my social impact' },
  { value: OrganizationExperienceHopeEnum['learn-grow-skills'], label: 'Learn and grow my skills' },
  { value: OrganizationExperienceHopeEnum['increase-funding'], label: 'Increase funding' },
  { value: OrganizationExperienceHopeEnum['grow-network'], label: 'Grow my network' },
  { value: OrganizationExperienceHopeEnum['grow-organization'], label: 'Grow my organization' },
  { value: OrganizationExperienceHopeEnum.other, label: 'Other' },
];

export const OrgSubmission = (props: ApplicationPageProps) => {
  const { onBack, progress, onSubmit, dataKey } = { ...props };
  const [submitted, setSubmitted] = useState(false);
  const [failed, setFailed] = useState(false);

  const { getApplicationData, getApplicationDataByKey, updateApplicationData } =
    useApplicationLocalStorage<OrganizationApplicationParameters>(dataKey);

  const {
    register,
    handleSubmit,
    formState,
    setValue,
    watch,
    control,
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      experienceHope: getApplicationDataByKey('experienceHope') || [],
      referralMethod: getApplicationDataByKey('referralMethod') || '',
    },
  });
  const { errors, isSubmitting, isSubmitSuccessful } = formState;

  const submitOrganizationMutation = Application.useSubmitOrganizationApplication();
  const queryClient = useQueryClient();

  const submit: SubmitHandler<FormValues> = (data) => {
    if (data.experienceHope && data.referralMethod) {
      // update app data so it is availble to profile update if needed
      updateApplicationData({
        experienceHope: data.experienceHope,
        referralMethod: data.referralMethod,
      });
      const submitData = getApplicationData();
      // profile update passes onSubmit prop, override new app submit in this case
      if (onSubmit) {
        try {
          onSubmit();
        } catch (e) {
          console.error('Data submission error', e);
          setFailed(true);
        }
      } else {
        submitOrganizationMutation.mutate(
          {
            data: submitData,
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({
                queryKey: Application.getGetOrganizationApplicationStatusQueryKey(),
              });
            },
            onError: () => {
              setFailed(true);
            },
          },
        );
      }
    } else {
      setSubmitted(true);
    }
  };

  return (
    <ApplicationBackground
      progress={progress}
      isOrganization={true}
      onBack={onBack}
      onSubmit={handleSubmit(submit)}
      customLeftPanelContent={<OrgSubmissionShowcase />}
      isEditProfile={props.isEditProfile}
      primaryCtaText={isSubmitSuccessful || isSubmitting ? 'Submitting' : 'Submit'}
      ctaDisabled={isSubmitSuccessful || isSubmitting}
    >
      <ApplicationPageContent
        title='Last question: What are you hoping to get out of this platform?'
        description="Select all that apply - let us know if there's any we missed."
      >
        <TagSelect
          tags={ORG_HOPE_OPTIONS}
          selectedTags={watch('experienceHope') || []}
          register={() =>
            register('experienceHope', {
              required: "Select at least one thing you're hoping to get out of this experience",
            })
          }
          error={errors.experienceHope?.message}
          onSelectionChange={(value: string[]) => {
            setValue('experienceHope', value as OrganizationExperienceHopeEnum[]);
          }}
        />

        {submitted && (
          <ErrorBox showAlertTriangle={true}>
            Select at least one thing you're hoping to get out of this experience
          </ErrorBox>
        )}

        <FormsInputDropdown
          control={control}
          name='referralMethod'
          rules={{
            required: 'Select how you found out about this opportunity to continue',
          }}
          label='How did you hear about MovingWorlds?'
          placeholder='Select one'
          items={REFERRAL_METHOD_OPTIONS}
          type='default'
          error={errors.referralMethod}
          defaultSelectedKey={watch('referralMethod') || undefined}
        />
        {failed && (
          <ErrorBox showAlertTriangle={true}>
            There was an error submitting your application. Please try again later.
          </ErrorBox>
        )}
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
